import UserImage from "@/components/ui/forms/UserImage";

import {useCallback, useEffect, useState} from "react";

import {isEqual, isValidEmail} from "@helpers";
import {useReportOptions} from "@hooks/Report";
import {useUserRole} from "@hooks/UserRole";
import {useCountries, useRegionOptions} from "@hooks/WorldCountries";
import photoImage from "@images/cards/photo.svg";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {FillDataPopup} from "@popups/attentionPopups/FillDataPopup";
import {SuccessfulSavePopup} from "@popups/attentionPopups/SuccessfulSavePopup";
import {ReportPopup} from "@popups/requestPopups/ReportPopup";
import {UploadImagePopup} from "@popups/uploadPopups/UploadImagePopup";
import {
  clearOptions,
  getCountries,
  getProductLine,
} from "@redux/slices/productSlices/optionsSlice";
import {
  clearCreate,
  createUser,
  current,
  setError,
  setIsChanged,
  setTarget,
} from "@redux/slices/userSlices/createSlice";
import {BackButton} from "@ui/buttons/BackButton";
import {EditField} from "@ui/forms/EditField";
import {EditTitle} from "@ui/forms/EditTitle";
import {BorderArticle} from "@ui/general/BorderArticle";
import {Spinner} from "@ui/general/Spinner";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import styles from "./styles.module.scss";

export const UserCreatePage = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {openPopup} = usePopup();

  const {target, status} = useSelector((state) => state.user.create);
  const {language} = useSelector((state) => state.common.settings);
  const {countries: countriesFromApi, productLine} = useSelector(
    (state) => state.product.options,
  );

  const [photoFile, setPhotoFile] = useState(null);
  const countries = useCountries(countriesFromApi);

  const {USER_REPORT_OPTIONS} = useReportOptions();
  const {USER_ROLE_OPTIONS} = useUserRole();

  const selectedCountryName = target?.country;
  const regionOptions = useRegionOptions(selectedCountryName, countriesFromApi);

  const updateUserField = useCallback(
    (fieldName, value) => {
      dispatch((dispatch, getState) => {
        const currentTarget = getState().user.create.target;
        if (fieldName === "role") {
          dispatch(
            setTarget({
              ...(currentTarget || {}),
              product_line_id: "",
              country: "",
              region: "",
              [fieldName]: value,
            }),
          );
        } else {
          dispatch(setTarget({...(currentTarget || {}), [fieldName]: value}));
        }
      });
    },
    [dispatch],
  );

  const handleClickImage = () => {
    openPopup(<UploadImagePopup setPhotoFile={setPhotoFile} />);
  };

  const handleDeletePhoto = () => {
    setPhotoFile(null);
    updateUserField("cover", null);
  };

  const isFormValid = () => {
    const requiredFields = ["name", "lastname", "email", "password", "role"];

    if (target?.role !== "Admin") {
      requiredFields.push("country", "product_line_id");
    }

    return requiredFields.every((field) => {
      const value = target?.[field];

      return value !== "" && value !== null && value !== undefined;
    });
  };

  const handleClickSave = async () => {
    if (!isFormValid()) {
      openPopup(<FillDataPopup />);

      return;
    } else if (!isValidEmail(target.email)) {
      return dispatch(setError(t("server_error.email")));
    }

    const countryObj = countries.find((c) => c.name === target.country);
    const countryId = countryObj ? countryObj.id : "";

    const user = {
      user: {
        name: target.name,
        lastname: target.lastname,
        email: target.email,
        password: target.password,
        type: target.role,
        country_id: countryId,
        region: target.region,
        product_line_id: target.product_line_id,
        cover: photoFile,
      },
    };

    const resultAction = await dispatch(createUser({user}));

    if (createUser.fulfilled.match(resultAction)) {
      openPopup(<SuccessfulSavePopup title={t("popup.save_data.user")} />);
      setPhotoFile(null);
      dispatch(clearCreate());
    } else {
      dispatch(setError("Save error!"));
    }
  };

  useEffect(() => {
    if (target && current) {
      dispatch(setIsChanged(!isEqual(target, current)));
    }
  }, [target, current, dispatch]);

  useEffect(() => {
    dispatch(getCountries());
    dispatch(getProductLine());

    return () => {
      dispatch(clearOptions());
      dispatch(clearCreate());
    };
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (photoFile) {
        URL.revokeObjectURL(photoFile);
      }
    };
  }, [photoFile]);

  return (
    <div className={"w-[100%]"}>
      <BorderArticle>
        <div className={styles.userWrapper}>
          <div className={styles.userHead}>
            <BackButton />
          </div>

          {status === "loading" ? (
            <Spinner
              width={80}
              height={80}
            />
          ) : (
            <div className={`${styles.user} ${styles.userBig}`}>
              <div className={styles.userInfo}>
                <EditTitle
                  label={"user-name"}
                  value={target?.name}
                  onChange={(e) => updateUserField("name", e.target.value)}
                  placeholder={t("input.user_name_placeholder")}
                  handleClickSave={handleClickSave}
                  disabledButton={false}
                />

                <UserImage
                  onClickDelete={handleDeletePhoto}
                  photoFile={photoFile}
                  photoImage={photoImage}
                  onClick={handleClickImage}
                />
              </div>

              <div className={styles.content}>
                <ul>
                  <EditField
                    label={`${t("input.last_name")}*`}
                    value={target?.lastname}
                    onChange={(e) =>
                      updateUserField("lastname", e.target.value)
                    }
                  />

                  <EditField
                    label={`${t("input.email")}*`}
                    value={target?.email}
                    onChange={(e) => updateUserField("email", e.target.value)}
                  />

                  <EditField
                    label={`${t("input.password")}*`}
                    value={target?.password}
                    onChange={(e) =>
                      updateUserField("password", e.target.value)
                    }
                  />

                  <EditField
                    label={`${t("input.role")}*`}
                    width={280}
                    height={"auto"}
                    options={USER_ROLE_OPTIONS}
                    onSelect={(selectedOption) =>
                      updateUserField("role", selectedOption?.value)
                    }
                    type={"select"}
                  />

                  {target?.role !== "Admin" && (
                    <>
                      {productLine && productLine.length > 0 && (
                        <EditField
                          label={`${t("input.product_line")}*`}
                          width={280}
                          height={150}
                          searchPlaceholder={t("input.product_line_search")}
                          activeOption={target?.product_line}
                          options={productLine}
                          onSelect={(selectedOption) =>
                            updateUserField(
                              "product_line_id",
                              selectedOption?.id,
                            )
                          }
                          type={"select"}
                        />
                      )}

                      <EditField
                        label={`${t("input.country")}*`}
                        width={280}
                        height={150}
                        searchPlaceholder={t("input.country_search")}
                        activeOption={target?.country}
                        options={countries}
                        onSelect={(selectedOption) => {
                          updateUserField("country", selectedOption?.name);
                          updateUserField("region", "");
                        }}
                        emptyResult={
                          <span
                            onClick={() =>
                              openPopup(
                                <ReportPopup options={USER_REPORT_OPTIONS} />,
                              )
                            }
                            dangerouslySetInnerHTML={{
                              __html: t("input.countries_regions_empty"),
                            }}
                          />
                        }
                        type={"select"}
                      />

                      {language === "ru" && selectedCountryName && (
                        <EditField
                          label={t("input.region")}
                          width={280}
                          height={150}
                          searchPlaceholder={t("input.region_search")}
                          activeOption={target?.region}
                          options={
                            Array.isArray(regionOptions) ? regionOptions : []
                          }
                          onSelect={(selectedOption) =>
                            updateUserField("region", selectedOption?.name)
                          }
                          emptyResult={
                            <span
                              onClick={() =>
                                openPopup(
                                  <ReportPopup options={USER_REPORT_OPTIONS} />,
                                )
                              }
                              dangerouslySetInnerHTML={{
                                __html: t("input.countries_regions_empty"),
                              }}
                            />
                          }
                          type={"select"}
                          selectKey={`region-${selectedCountryName || "none"}`}
                          resetKey={`${selectedCountryName || "none"}-${regionOptions?.length || 0}`}
                        />
                      )}
                    </>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </BorderArticle>
    </div>
  );
};
